'use client';

import { useNotification } from '@egalite/ui/IncluiPCD/NotificationProvider';
import { FC, ReactNode, useEffect, useRef } from 'react';
import { proxy } from 'valtio';

export const liveStore = proxy<{ liveId?: string; startAt?: string | null }>({ liveId: null, startAt: null });

type Props = {
	children?: ReactNode;
};

const LiveUpdater: FC<Props> = ({ children }) => {
	const notification = useNotification();

	// Um aviso por palestra (a chave é o horário dela). O long-polling responde a
	// cada ~70s, então sem essa guarda os 10 minutos anteriores ao início rendem
	// um toast por ciclo — e como o Radix pausa o auto-dismiss enquanto a janela
	// está sem foco, numa aba parada eles se acumulam na tela em vez de expirar.
	const notifiedLive = useRef<string>();

	useEffect(() => {
		let cancelled = false;

		const poll = async () => {
			while (!cancelled) {
				try {
					const result = await fetch('/api/v2/utils/lives-long-polling', { cache: 'no-store' });

					const data = await result.json();

					if (result.status !== 200) throw new Error(data?.errors);

					const liveId = data?.data?.videoId;
					const startAt = data?.data?.startAt ?? null;

					// O aviso usa o nextStartAt (próxima palestra ainda por começar), não o
					// startAt do destaque: o destaque só troca 1h depois do início da
					// palestra anterior, então perto da troca o startAt é o da que já passou.
					const nextStartAt = data?.data?.nextStartAt ?? null;
					const nextStartDifference = (Date.parse(nextStartAt) || 0) - Date.now();
					const tenMinutes = 1000 * 60 * 10;

					if (nextStartDifference <= tenMinutes && nextStartDifference > 0 && notifiedLive.current !== nextStartAt) {
						notifiedLive.current = nextStartAt;

						notification.success('Uma nova live está prestes a começar!', {
							link: 'Clique aqui para acessar',
							href: `/programacao`,
							duration: 15_000,
						});
					}

					if (liveId !== liveStore.liveId) {
						liveStore.liveId = liveId;
					}

					if (startAt !== liveStore.startAt) {
						liveStore.startAt = startAt;
					}

				} catch (err) {
					console.error(err);

					await new Promise(resolve => setTimeout(resolve, 60_000));
				}
			}
		};

		// Adia o início do long-polling para depois que a página estiver ociosa,
		// evitando concorrer por rede/CPU durante o carregamento crítico.
		// A live continua sendo atualizada normalmente, apenas alguns instantes
		// depois do primeiro paint.
		const w = window as Window & { requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number };
		const start = () => {
			void poll();
		};

		let idleId: number | undefined;
		let timeoutId: ReturnType<typeof setTimeout> | undefined;
		if (typeof w.requestIdleCallback === 'function') {
			idleId = w.requestIdleCallback(start, { timeout: 3000 });
		} else {
			timeoutId = setTimeout(start, 2000);
		}

		return () => {
			cancelled = true;
			const cancelIdle = (window as Window & { cancelIdleCallback?: (id: number) => void }).cancelIdleCallback;
			if (idleId !== undefined && typeof cancelIdle === 'function') cancelIdle(idleId);
			if (timeoutId !== undefined) clearTimeout(timeoutId);
		};
	}, []);

	return <>{children}</>;
};

export default LiveUpdater;
